import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { collection, query, where, getDocs, Timestamp, doc, updateDoc, deleteDoc, addDoc, getDoc, writeBatch } from 'firebase/firestore';
import { DndContext, DragOverlay, closestCenter, PointerSensor, MouseSensor, useSensor, useSensors, useDroppable, pointerWithin, closestCorners } from '@dnd-kit/core';
import { SortableContext, useSortable, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { X, Clock, Plus, Check, AlertCircle, ArrowLeft } from 'lucide-react';
import { db as firebaseDb } from '../firebase';
import ClientAddAppointmentModal from './ClientAddAppointmentModal';
import ClientEditAppointmentModal from './ClientEditAppointmentModal';
import RecurrenceEditModal from './RecurrenceEditModal';
import RecurrenceMoveModal from './RecurrenceMoveModal';
import AppointmentViewModal from './AppointmentViewModal';
import ClientDayView from './ClientDayView';
import { useTheme } from '../contexts/ThemeContext';
import { generateRecurringAppointments } from '../utils/recurrenceUtils';
import './ClientWeekView.css';

// Fonction utilitaire pour comparer deux dates
const isSameDay = (date1, date2) => {
  if (!(date1 instanceof Date) || !(date2 instanceof Date)) {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate();
  }
  return date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate();
};

// Fonction utilitaire pour vérifier si une couleur est claire
const isLightColor = (color) => {
  if (!color) return false;
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  return brightness > 155;
};

// Composant pour un rendez-vous draggable
const SortableAppointment = ({
  appointment,
  position,
  getStatusClass,
  formatTime,
  handleAppointmentClick,
  handleAppointmentDoubleClick,
  canEditAppointments,
  employeeColor
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: appointment.id,
    disabled: !canEditAppointments || appointment.status === 'completed'
  });

  const style = {
    left: `${position.left}%`,
    width: `${position.width}%`,
    position: 'absolute',
    top: '50%',
    transform: CSS.Transform.toString(transform) || 'translateY(-50%)',
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: canEditAppointments && appointment.status !== 'completed' ? 'grab' : 'pointer',
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        zIndex: isDragging ? 1000 : 1,
      }}
      {...attributes}
      {...listeners}
      className={`cwv-timeline-appointment ${getStatusClass(appointment.status)} ${isDragging ? 'cwv-dragging' : ''} ${canEditAppointments && appointment.status !== 'completed' ? 'cwv-draggable' : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        if (!isDragging) {
          handleAppointmentClick(appointment);
        }
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        if (!isDragging) {
          handleAppointmentDoubleClick(appointment);
        }
      }}
    >
      <div className="cwv-appointment-content">
        <div className="cwv-appointment-time">
          {`${formatTime(appointment.startTime)} - ${formatTime(appointment.endTime)}`}
        </div>
        {appointment.details && (
          <div className="cwv-appointment-details">
            {appointment.details}
          </div>
        )}
        {appointment.notes && appointment.notes.content && (
          <div className="cwv-notes-indicator">
            📝
          </div>
        )}
        {appointment.isRecurring && (
          <div className="cwv-recurring-indicator" title="Rendez-vous récurrent">
            🔄
          </div>
        )}
      </div>
    </div>
  );
};

// Composant pour une zone droppable
const DroppableRow = ({ id, children, className, onClick }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: id,
  });

  return (
    <div
      ref={setNodeRef}
      className={`${className} ${isOver ? 'cwv-dragging-over' : ''}`}
      onClick={onClick}
    >
      {children}
    </div>
  );
};

const ClientWeekView = React.memo(({
  db,
  client,
  currentDate,
  startOfWeek: propStartOfWeek,
  appointments: propAppointments,
  employees: propEmployees,
  generalSettings,
  formatDate,
  formatTime,
  onAppointmentClick,
  changeDate,
  isLightColor: propIsLightColor,
  onAddAppointment,
  onUpdateAppointment,
  onDeleteAppointment,
  onHandleAddAppointment,
  adjustForTimezone,
  hasPermission
}) => {
  const safeDb = db || firebaseDb;
  const { theme } = useTheme();
  const [activeId, setActiveId] = useState(null);
  const [internalAppointments, setInternalAppointments] = useState([]); // État interne si non fourni
  const [internalEmployees, setInternalEmployees] = useState([]); // État interne si non fourni
  const [weekDates, setWeekDates] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [approvedLeaves, setApprovedLeaves] = useState([]);
  const [settings, setSettings] = useState({
    planningOpeningTime: '08:00',
    planningClosingTime: '18:00',
    showWeekends: true,
    firstDayOfWeek: 1,
  });

  // Utiliser les props si fournies, sinon l'état interne
  const appointments = propAppointments || internalAppointments;
  const setAppointments = setInternalAppointments;
  const employees = propEmployees || internalEmployees;
  const setEmployees = setInternalEmployees;

  // Utiliser propStartOfWeek s'il existe (ClientDetails), sinon currentDate (ClientPlanning)
  const effectiveCurrentDate = propStartOfWeek || currentDate || new Date();
  const [showRecurrenceMoveModal, setShowRecurrenceMoveModal] = useState(false);
  const [showRecurrenceEditModal, setShowRecurrenceEditModal] = useState(false);
  const [pendingMoveData, setPendingMoveData] = useState(null);
  const [pendingUpdateData, setPendingUpdateData] = useState(null);
  const [clickTimeout, setClickTimeout] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [selectedDayView, setSelectedDayView] = useState(null); // null = vue semaine, Date = vue jour détaillée
  const [isCopyMode, setIsCopyMode] = useState(false); // Mode copie activé avec Ctrl
  const copyModeRef = useRef(false); // Ref pour stocker l'état de Ctrl pendant le drag

  // Fonction par défaut pour formatTime si non fournie
  const defaultFormatTime = (time) => {
    if (!time) return '';
    return time;
  };

  const formatTimeFunc = formatTime || defaultFormatTime;

  // Gestion responsive
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Gestion de la touche Ctrl pour le mode copie
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey) { // metaKey pour Mac (Cmd)
        setIsCopyMode(true);
        copyModeRef.current = true;
      }
    };

    const handleKeyUp = (e) => {
      if (!e.ctrlKey && !e.metaKey) {
        setIsCopyMode(false);
        copyModeRef.current = false;
      }
    };

    const handleBlur = () => {
      setIsCopyMode(false);
      copyModeRef.current = false;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  // Gestion des permissions - Par défaut true si hasPermission n'est pas défini
  const canEditAppointments = hasPermission ? hasPermission({ resource: 'appointments', action: 'write' }) : true;

  // Configuration des sensors pour le drag and drop
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8, // Distance minimale pour activer le drag (permet le double-clic)
      },
    }),
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Distance minimale pour activer le drag (permet le double-clic)
      },
    })
  );

  // Fonction pour générer les dates de la semaine
  const updateWeekDates = useCallback((date) => {
    const weekStart = new Date(date);
    const day = weekStart.getDay();
    const diff = (day < settings.firstDayOfWeek ? 7 : 0) + day - settings.firstDayOfWeek;
    weekStart.setDate(weekStart.getDate() - diff);
    weekStart.setHours(0, 0, 0, 0); // Crucial : réinitialiser l'heure
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(weekStart);
      day.setDate(weekStart.getDate() + i);
      dates.push(day);
    }
    setWeekDates(dates);
  }, [settings.firstDayOfWeek]);

  const fetchEmployees = useCallback(async () => {
    if (!safeDb) return;
    try {
      const employeesRef = collection(safeDb, 'employees');
      const querySnapshot = await getDocs(employeesRef);
      const fetchedEmployees = querySnapshot.docs.map(doc => ({
        userId: doc.id,
        ...doc.data(),
        color: doc.data().color || '#808080',
        photoURL: doc.data().photoURL || null
      }));

      const filteredEmployees = fetchedEmployees.filter(employee => {
        if (employee.role === 'beneficiaire') {
          return false;
        }
        const isNotArchived = employee.isArchived !== true;
        const isActive = employee.isActive !== false;
        return isNotArchived && isActive;
      });

      setEmployees(filteredEmployees);
    } catch (error) {
      // Erreur silencieuse lors de la récupération des employés
    }
  }, [safeDb]);

  const fetchAppointments = useCallback(async () => {
    if (!safeDb || !client.id || weekDates.length === 0) return;
    try {
      const startOfWeekDate = new Date(weekDates[0]);
      startOfWeekDate.setHours(0, 0, 0, 0);
      const endOfWeekDate = new Date(weekDates[6]);
      endOfWeekDate.setHours(23, 59, 59, 999);

      const appointmentsRef = collection(safeDb, 'appointments');
      const q = query(
        appointmentsRef,
        where('clientId', '==', client.id),
        where('date', '>=', Timestamp.fromDate(startOfWeekDate)),
        where('date', '<=', Timestamp.fromDate(endOfWeekDate))
      );
      const querySnapshot = await getDocs(q);
      const fetchedAppointments = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate() || new Date(),
      }));
      setAppointments(fetchedAppointments);
    } catch (error) {
      // Erreur silencieuse lors de la récupération des rendez-vous
    }
  }, [safeDb, client.id, weekDates]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  useEffect(() => {
    updateWeekDates(effectiveCurrentDate);
  }, [effectiveCurrentDate, updateWeekDates]);

  useEffect(() => {
    if (weekDates.length > 0) {
      fetchAppointments();
    }
  }, [fetchAppointments, weekDates, refreshTrigger]);

  useEffect(() => {
    const fetchSettings = async () => {
      if (!safeDb) return;
      const settingsDoc = await getDoc(doc(safeDb, 'settings', 'general'));
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setSettings({
          planningOpeningTime: data.planningOpeningTime || '08:00',
          planningClosingTime: data.planningClosingTime || '18:00',
          showWeekends: data.showWeekends !== false,
          firstDayOfWeek: data.firstDayOfWeek || 1,
        });
      }
    };
    fetchSettings();
  }, [safeDb]);

  // Récupération des congés approuvés
  const fetchApprovedLeaves = useCallback(async () => {
    if (!db) return;
    try {
      const leavesRef = collection(db, 'leaves');
      const q = query(leavesRef, where('status', '==', 'approved'));
      const querySnapshot = await getDocs(q);
      const leaves = querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,
        startDate: doc.data().startDate.toDate(),
        endDate: doc.data().endDate.toDate()
      }));
      setApprovedLeaves(leaves);
    } catch (error) {
      // Erreur silencieuse lors de la récupération des congés
    }
  }, [safeDb]);

  useEffect(() => {
    fetchApprovedLeaves();
  }, [fetchApprovedLeaves]);

  // Fonction pour vérifier si un employé est en congé à une date donnée
  const isEmployeeOnLeave = useCallback((employeeId, date) => {
    if (!approvedLeaves.length) return false;

    const targetDate = new Date(date);
    targetDate.setHours(0, 0, 0, 0);

    return approvedLeaves.some(leave => {
      const startDate = new Date(leave.startDate);
      const endDate = new Date(leave.endDate);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);

      return leave.employeeId === employeeId &&
        targetDate >= startDate &&
        targetDate <= endDate;
    });
  }, [approvedLeaves]);

  // Fonction pour vérifier si aujourd'hui
  const isToday = useCallback((date) => {
    const today = new Date();
    return isSameDay(date, today);
  }, []);

  // Fonction utilitaire pour convertir un temps en minutes
  const timeToMinutes = (time) => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const openingMinutes = timeToMinutes(settings.planningOpeningTime);
  const closingMinutes = timeToMinutes(settings.planningClosingTime);
  const totalMinutes = closingMinutes - openingMinutes;

  // Fonction pour générer les créneaux horaires de la journée
  const generateDayTimeline = () => {
    const slots = [];
    let currentTime = new Date(`2000-01-01T${settings.planningOpeningTime}`);
    const endTime = new Date(`2000-01-01T${settings.planningClosingTime}`);

    while (currentTime <= endTime) {
      slots.push(currentTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }));
      currentTime.setMinutes(currentTime.getMinutes() + 30);
    }

    return slots;
  };

  const dayTimeline = generateDayTimeline();

  // Fonction pour calculer la position et la largeur d'un rendez-vous dans la timeline
  const getAppointmentPosition = (appointment) => {
    const startMinutes = timeToMinutes(appointment.startTime);
    const endMinutes = timeToMinutes(appointment.endTime);

    const totalSlots = dayTimeline.length;
    const minutesFromStart = startMinutes - openingMinutes;
    const durationMinutes = endMinutes - startMinutes;

    const startSlot = minutesFromStart / 30;
    const durationInSlots = durationMinutes / 30;

    const left = (startSlot / totalSlots) * 100;
    const width = (durationInSlots / totalSlots) * 100;

    const finalLeft = Math.max(0, Math.min(left, 100));
    const finalWidth = Math.max(0, Math.min(width, 100 - finalLeft));

    return {
      left: finalLeft,
      width: finalWidth
    };
  };

  // Gestion des clics
  const handleAppointmentClick = (appointment) => {
    // Si un timeout existe déjà, c'est un double-clic
    if (clickTimeout) {
      clearTimeout(clickTimeout);
      setClickTimeout(null);

      // Double-clic détecté - ouvrir le modal d'édition
      if (canEditAppointments && appointment.status !== 'completed') {
        setSelectedAppointment(appointment);
        setIsEditModalOpen(true);
      }
      return;
    }

    // Premier clic - attendre pour voir s'il y a un deuxième clic
    const timeout = setTimeout(() => {
      setSelectedAppointment(appointment);
      setIsViewModalOpen(true);
      setClickTimeout(null);
    }, 300);

    setClickTimeout(timeout);
  };

  const handleCellClick = (date, employeeId) => {
    if (!canEditAppointments) return;
    setSelectedDate(date);
    setSelectedEmployee(employeeId);
    setSelectedAppointment(null);
    setIsModalOpen(true);
  };

  const handleAppointmentDoubleClick = () => {
    // Cette fonction n'est plus utilisée car le double-clic est géré dans handleClick
  };


  // Fonction pour obtenir l'initiale d'un nom
  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Fonctions pour obtenir les rendez-vous
  const getEmployeeAppointmentsForDay = (employeeId, date) => {
    return appointments.filter(app =>
      app.employeeId === employeeId &&
      app.status !== 'libre' &&
      isSameDay(app.date, date)
    );
  };

  const getUnassignedAppointmentsForDay = (date) => {
    return appointments.filter(app =>
      (!app.employeeId || app.status === 'libre') &&
      isSameDay(app.date, date)
    );
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'in_progress':
        return 'cwv-in-progress';
      case 'completed':
        return 'cwv-completed';
      case 'assigne':
        return 'cwv-assigned';
      case 'libre':
        return 'cwv-unassigned';
      case 'reserve':
        return 'cwv-reserved';
      default:
        return '';
    }
  };

  // Gestion du drag and drop
  const handleDragStart = useCallback((event) => {
    setActiveId(event.active.id);
    // Capturer l'état de Ctrl au moment du début du drag
    const isDraggingWithCtrl = event.activatorEvent?.ctrlKey || event.activatorEvent?.metaKey || copyModeRef.current;
    setIsCopyMode(isDraggingWithCtrl);
    copyModeRef.current = isDraggingWithCtrl;
  }, []);

  const handleDragEnd = useCallback(async (event) => {
    setActiveId(null);

    if (!canEditAppointments) {
      setIsCopyMode(false);
      copyModeRef.current = false;
      return;
    }

    const { active, over } = event;

    // Vérifier si Ctrl était enfoncé au début du drag (stocké dans la ref)
    const isCtrlPressed = copyModeRef.current;

    if (!over) {
      setIsCopyMode(false);
      copyModeRef.current = false;
      return;
    }
    if (active.id === over.id) {
      setIsCopyMode(false);
      copyModeRef.current = false;
      return;
    }

    const appointment = appointments.find(app => app.id === active.id);
    if (!appointment) {
      setIsCopyMode(false);
      copyModeRef.current = false;
      return;
    }

    // Parse l'ID de la zone de drop
    let targetId = over.id;
    const targetData = over.data.current;

    // Si on a laché sur un rendez-vous (sortable), on essaye de trouver le droppable parent (la cellule)
    if (targetData?.containerId) {
      targetId = targetData.containerId;
    } else if (targetData?.sortable?.containerId && targetData.sortable.containerId !== 'global-week-context') {
      targetId = targetData.sortable.containerId;
    }

    const [targetEmployeeId, dayIndexStr] = targetId.split('-');
    const dayIndex = parseInt(dayIndexStr, 10);

    if (isNaN(dayIndex) || dayIndex < 0 || dayIndex >= 7) {
      // Index de jour invalide
      setIsCopyMode(false);
      copyModeRef.current = false;
      return;
    }

    const targetDate = new Date(weekDates[dayIndex]);
    targetDate.setHours(0, 0, 0, 0); // Sécurité anti-décalage
    const newEmployeeId = targetEmployeeId === 'unassigned' ? null : targetEmployeeId;

    // Empêcher le déplacement vers une cellule de congé
    if (newEmployeeId && isEmployeeOnLeave(newEmployeeId, targetDate)) {
      // Impossible de déplacer vers un employé en congé
      setIsCopyMode(false);
      copyModeRef.current = false;
      return;
    }

    // MODE COPIE : Créer un nouveau rendez-vous
    if (isCtrlPressed) {
      await copyAppointment(appointment, targetDate, newEmployeeId);
      setIsCopyMode(false);
      copyModeRef.current = false;
      return;
    }

    // MODE DÉPLACEMENT : Comportement normal
    setIsCopyMode(false);
    copyModeRef.current = false;

    // Conserver l'heure originale
    const appDate = appointment.date instanceof Date ? appointment.date : (appointment.date?.toDate ? appointment.date.toDate() : new Date(appointment.date));
    const startOfDay = new Date(appDate);
    startOfDay.setHours(0, 0, 0, 0);
    const timeOfDay = appDate.getTime() - startOfDay.getTime();
    const finalDate = new Date(targetDate.getTime() + timeOfDay);

    // Si c'est un rendez-vous récurrent, afficher le modal de confirmation
    if (appointment.isRecurring) {
      setPendingMoveData({
        appointment: { ...appointment, date: appDate }, // S'assurer que date est un objet Date
        targetDate: finalDate,
        newEmployeeId,
      });
      setShowRecurrenceMoveModal(true);
      return;
    }

    // Pour les rendez-vous non récurrents, déplacer directement
    await moveAppointment(appointment, finalDate, newEmployeeId);
  }, [appointments, canEditAppointments, weekDates, isEmployeeOnLeave]);

  const moveAppointment = async (appointment, targetDate, newEmployeeId) => {
    try {
      const newStatus = newEmployeeId ? 'assigne' : 'libre';

      let employeeName = '';
      if (newEmployeeId) {
        const employee = employees.find(emp => emp.userId === newEmployeeId);
        if (employee) {
          employeeName = employee.name || `${employee.firstName} ${employee.lastName}`;
        }
      }

      await updateDoc(doc(safeDb, 'appointments', appointment.id), {
        date: Timestamp.fromDate(targetDate),
        employeeId: newEmployeeId,
        employeeName: employeeName,
        status: newStatus
      });

      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      // Erreur silencieuse
    }
  };

  const copyAppointment = async (appointment, targetDate, newEmployeeId) => {
    try {
      const newStatus = newEmployeeId ? 'assigne' : 'libre';

      let employeeName = '';
      if (newEmployeeId) {
        const employee = employees.find(emp => emp.userId === newEmployeeId);
        if (employee) {
          employeeName = employee.name || `${employee.firstName} ${employee.lastName}`;
        }
      }

      // Créer un nouveau rendez-vous (copie) sans l'ID et sans récurrence
      const newAppointment = {
        clientId: appointment.clientId,
        date: Timestamp.fromDate(targetDate),
        startTime: appointment.startTime,
        endTime: appointment.endTime,
        status: newStatus,
        // Ne pas copier les champs de récurrence
        isRecurring: false,
      };

      // Ajouter les champs optionnels uniquement s'ils existent
      if (appointment.details) {
        newAppointment.details = appointment.details;
      }
      if (appointment.notes) {
        newAppointment.notes = appointment.notes;
      }
      if (newEmployeeId) {
        newAppointment.employeeId = newEmployeeId;
        newAppointment.employeeName = employeeName;
      }

      await addDoc(collection(safeDb, 'appointments'), newAppointment);

      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      // Erreur silencieuse
    }
  };

  const handleRecurrenceMoveConfirm = async (data) => {
    if (!pendingMoveData) return;

    try {
      const { appointment, targetDate, newEmployeeId } = pendingMoveData;
      const { scope, endDate } = data;

      if (scope === 'this') {
        // Déplacer uniquement ce rendez-vous
        await moveAppointment(appointment, targetDate, newEmployeeId);
      } else if (scope === 'future') {
        // Déplacer ce rendez-vous et tous les suivants
        const allRecurringAppointments = await fetchAllRecurringAppointments(appointment);
        const currentDate = appointment.date instanceof Date ? appointment.date : (appointment.date?.toDate ? appointment.date.toDate() : new Date(appointment.date));
        const pivotDate = new Date(currentDate);
        pivotDate.setHours(0, 0, 0, 0);

        const appointmentsToUpdate = allRecurringAppointments.filter(app => {
          const appDate = app.date instanceof Date ? app.date : (app.date?.toDate ? app.date.toDate() : new Date(app.date));
          const compareDate = new Date(appDate);
          compareDate.setHours(0, 0, 0, 0);
          return compareDate >= pivotDate;
        });

        const batch = writeBatch(db);
        const displacementDelta = targetDate.getTime() - currentDate.getTime();

        let employeeName = '';
        if (newEmployeeId) {
          const employee = employees.find(emp => emp.userId === newEmployeeId);
          if (employee) {
            employeeName = employee.name || `${employee.firstName} ${employee.lastName}`;
          }
        }

        appointmentsToUpdate.forEach(app => {
          const appDate = app.date instanceof Date ? app.date : (app.date?.toDate ? app.date.toDate() : new Date(app.date));
          const newDate = new Date(appDate.getTime() + displacementDelta);
          batch.update(doc(db, 'appointments', app.id), {
            date: Timestamp.fromDate(newDate),
            employeeId: newEmployeeId,
            employeeName: employeeName,
            status: newEmployeeId ? 'assigne' : 'libre'
          });
        });

        await batch.commit();
      } else if (scope === 'period' && endDate) {
        // Déplacer les rendez-vous jusqu'à la date de fin spécifiée
        const allRecurringAppointments = await fetchAllRecurringAppointments(appointment);
        const currentDate = appointment.date instanceof Date ? appointment.date : (appointment.date?.toDate ? appointment.date.toDate() : new Date(appointment.date));
        const pivotDate = new Date(currentDate);
        pivotDate.setHours(0, 0, 0, 0);

        const periodEndDate = new Date(endDate);
        periodEndDate.setHours(23, 59, 59, 999);

        const appointmentsToUpdate = allRecurringAppointments.filter(app => {
          const appDate = app.date instanceof Date ? app.date : (app.date?.toDate ? app.date.toDate() : new Date(app.date));
          const compareDate = new Date(appDate);
          compareDate.setHours(0, 0, 0, 0);
          return compareDate >= pivotDate && compareDate <= periodEndDate;
        });

        const batch = writeBatch(db);
        const displacementDelta = targetDate.getTime() - currentDate.getTime();

        let employeeName = '';
        if (newEmployeeId) {
          const employee = employees.find(emp => emp.userId === newEmployeeId);
          if (employee) {
            employeeName = employee.name || `${employee.firstName} ${employee.lastName}`;
          }
        }

        appointmentsToUpdate.forEach(app => {
          const appDate = app.date instanceof Date ? app.date : (app.date?.toDate ? app.date.toDate() : new Date(app.date));
          const newDate = new Date(appDate.getTime() + displacementDelta);
          batch.update(doc(db, 'appointments', app.id), {
            date: Timestamp.fromDate(newDate),
            employeeId: newEmployeeId,
            employeeName: employeeName,
            status: newEmployeeId ? 'assigne' : 'libre'
          });
        });

        await batch.commit();
      }

      setShowRecurrenceMoveModal(false);
      setPendingMoveData(null);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error("Erreur lors du déplacement de la série récurrente:", error);
    }
  };

  const fetchAllRecurringAppointments = async (appointment) => {
    try {
      if (appointment.recurrenceSeriesId) {
        const appointmentsRef = collection(db, 'appointments');
        const q = query(
          appointmentsRef,
          where('clientId', '==', client.id),
          where('recurrenceSeriesId', '==', appointment.recurrenceSeriesId)
        );
        const querySnapshot = await getDocs(q);

        return querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          date: doc.data().date?.toDate() || new Date(),
        }));
      }
      return [appointment];
    } catch (error) {
      // Erreur silencieuse lors de la récupération des rendez-vous récurrents
      return [appointment];
    }
  };

  // Les fonctions de récurrence sont maintenant importées depuis recurrenceUtils.js
  // Cela évite la duplication de code et garantit la cohérence entre toutes les vues

  const handleAddAppointment = async (appointmentData) => {
    if (!canEditAppointments) return;
    try {
      let appointmentsToAdd = [];
      if (appointmentData.isRecurring) {
        appointmentsToAdd = generateRecurringAppointments(appointmentData, safeDb);
      } else {
        appointmentsToAdd = [{
          ...appointmentData,
          isRecurring: false,
          status: appointmentData.employeeId ? 'assigne' : 'libre'
        }];
      }

      const batch = writeBatch(safeDb);

      appointmentsToAdd.forEach(appointment => {
        const newAppointmentRef = doc(collection(safeDb, 'appointments'));

        let employeeName = '';
        if (appointment.employeeId) {
          const employee = employees.find(emp => emp.userId === appointment.employeeId);
          if (employee) {
            employeeName = employee.name || `${employee.firstName} ${employee.lastName}`;
          }
        }

        batch.set(newAppointmentRef, {
          ...appointment,
          clientId: client.id,
          employeeName: employeeName,
          date: Timestamp.fromDate(appointment.date)
        });
      });

      await batch.commit();

      setIsModalOpen(false);
      setSelectedDate(null);
      setSelectedEmployee(null);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      // Erreur silencieuse lors de l'ajout du/des rendez-vous
    }
  };

  const handleUpdateAppointment = async (updatedAppointment) => {
    if (!canEditAppointments) return;

    if (updatedAppointment.isRecurring) {
      setPendingUpdateData(updatedAppointment);
      setShowRecurrenceEditModal(true);
      return;
    }

    await updateSingleAppointment(updatedAppointment);
  };

  const updateSingleAppointment = async (appointment) => {
    try {
      const appointmentRef = doc(db, 'appointments', appointment.id);
      const updateData = {
        ...appointment,
        date: Timestamp.fromDate(appointment.date),
        status: appointment.employeeId ? 'assigne' : 'libre',
        recurrenceSeriesId: null
      };

      await updateDoc(appointmentRef, updateData);

      setIsEditModalOpen(false);
      setSelectedAppointment(null);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      // Erreur silencieuse lors de la mise à jour du rendez-vous
    }
  };

  const handleRecurrenceEditConfirm = async (scope) => {
    if (!pendingUpdateData) return;

    try {
      if (scope === 'this') {
        await updateSingleAppointment(pendingUpdateData);
      } else if (scope === 'future') {
        // Modifier ce rendez-vous et tous les suivants
        const allAppointments = await fetchAllRecurringAppointments(pendingUpdateData);
        const currentDate = new Date(pendingUpdateData.date);
        currentDate.setHours(0, 0, 0, 0);

        const appointmentsToUpdate = allAppointments.filter(app => {
          const appDate = new Date(app.date);
          appDate.setHours(0, 0, 0, 0);
          return appDate >= currentDate;
        });

        const batch = writeBatch(db);
        appointmentsToUpdate.forEach(appointment => {
          const appointmentRef = doc(db, 'appointments', appointment.id);
          const updateData = {
            ...pendingUpdateData,
            id: appointment.id,
            date: Timestamp.fromDate(new Date(appointment.date)),
            status: pendingUpdateData.employeeId ? 'assigne' : 'libre'
          };
          batch.update(appointmentRef, updateData);
        });

        await batch.commit();
      }

      setIsEditModalOpen(false);
      setShowRecurrenceEditModal(false);
      setPendingUpdateData(null);
      setSelectedAppointment(null);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      // Erreur silencieuse lors de la mise à jour des rendez-vous récurrents
    }
  };

  const handleDeleteAppointment = async (appointmentId, scope = 'this') => {
    if (!canEditAppointments) return;
    try {
      if (scope === 'this') {
        // Supprimer uniquement ce rendez-vous
        await deleteDoc(doc(db, 'appointments', appointmentId));
      } else if (scope === 'future') {
        // Supprimer ce rendez-vous et tous les suivants
        const appointment = appointments.find(app => app.id === appointmentId);
        if (appointment && appointment.recurrenceSeriesId) {
          const appointmentsRef = collection(db, 'appointments');
          const q = query(
            appointmentsRef,
            where('clientId', '==', client.id),
            where('recurrenceSeriesId', '==', appointment.recurrenceSeriesId)
          );
          const querySnapshot = await getDocs(q);

          const batch = writeBatch(db);
          const currentDate = new Date(appointment.date);
          currentDate.setHours(0, 0, 0, 0);

          querySnapshot.docs.forEach(doc => {
            const appData = doc.data();
            const appDate = appData.date?.toDate() || new Date();
            appDate.setHours(0, 0, 0, 0);

            if (appDate >= currentDate) {
              batch.delete(doc.ref);
            }
          });

          await batch.commit();
        }
      }
      setIsEditModalOpen(false);
      setIsViewModalOpen(false);
      setSelectedAppointment(null);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      // Erreur silencieuse lors de la suppression du rendez-vous
    }
  };

  // Récupérer le rendez-vous actif pour le DragOverlay
  const activeAppointment = activeId ? appointments.find(app => app.id === activeId) : null;

  // Obtenir tous les IDs pour le contexte de drag
  const allAppointmentIds = appointments.map(app => app.id);

  // Calculer les statistiques de la semaine
  const weekStats = useMemo(() => {
    const totalAppointments = appointments.length;
    const assignedAppointments = appointments.filter(app => app.status === 'assigne').length;
    const unassignedAppointments = appointments.filter(app => !app.employeeId || app.status === 'libre').length;
    const completedAppointments = appointments.filter(app => app.status === 'completed').length;

    let totalHours = 0;
    appointments.forEach(app => {
      const start = timeToMinutes(app.startTime);
      const end = timeToMinutes(app.endTime);
      totalHours += (end - start) / 60;
    });

    return {
      total: totalAppointments,
      assigned: assignedAppointments,
      unassigned: unassignedAppointments,
      completed: completedAppointments,
      totalHours: totalHours.toFixed(1),
      coverageRate: totalAppointments > 0 ? ((assignedAppointments / totalAppointments) * 100).toFixed(0) : 0
    };
  }, [appointments]);

  // Handler pour cliquer sur un jour de la semaine
  const handleDayClick = (date) => {
    setSelectedDayView(date);
  };

  // Handler pour revenir à la vue semaine
  const handleBackToWeek = () => {
    setSelectedDayView(null);
  };

  // Si un jour est sélectionné, afficher la vue jour détaillée
  if (selectedDayView) {
    return (
      <div className="cwv-container">
        {/* Bouton retour */}
        <div className="cwv-day-view-header">
          <button onClick={handleBackToWeek} className="cwv-back-button">
            <ArrowLeft size={20} />
            <span>Retour à la semaine</span>
          </button>
          <div className="cwv-day-view-title">
            {selectedDayView.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </div>
        </div>

        {/* Vue jour avec ClientDayView */}
        <div className="cwv-day-view-content">
          <ClientDayView
            db={db}
            client={client}
            currentDate={selectedDayView}
            formatDate={formatDate}
            formatTime={formatTimeFunc}
            hasPermission={hasPermission}
          />
        </div>
      </div>
    );
  }

  // Composant pour un rendez-vous en mode liste (vertical) - Style identique à ClientDayView
  const AppointmentCard = ({ appointment, employee }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({
      id: appointment.id,
      disabled: !canEditAppointments || appointment.status === 'completed',
      data: {
        appointment,
        containerId: employee?.userId ? `${employee.userId}-${new Date(appointment.date).getDay()}` : `unassigned-${new Date(appointment.date).getDay()}`
      }
    });

    // Style identique à ClientDayView - fond coloré avec bordure 2px
    let backgroundColor, borderColor, textColor;

    switch (appointment.status) {
      case 'libre':
      case 'unassigned':
        backgroundColor = 'var(--cwv-unassigned-bg)';
        borderColor = 'var(--cwv-unassigned-border)';
        textColor = 'var(--cwv-unassigned-text)';
        break;
      case 'assigne':
      case 'assigned':
        backgroundColor = 'var(--cwv-assigned-bg)';
        borderColor = 'var(--cwv-assigned-border)';
        textColor = 'var(--cwv-assigned-text)';
        break;
      case 'reserve':
      case 'reserved':
        backgroundColor = 'var(--cwv-reserved-bg)';
        borderColor = 'var(--cwv-reserved-border)';
        textColor = 'var(--cwv-reserved-text)';
        break;
      case 'completed':
        backgroundColor = 'var(--cwv-completed-bg)';
        borderColor = 'var(--cwv-completed-border)';
        textColor = 'var(--cwv-completed-text)';
        break;
      case 'in-progress':
        backgroundColor = 'var(--cwv-in-progress-bg)';
        borderColor = 'var(--cwv-in-progress-border)';
        textColor = 'var(--cwv-in-progress-text)';
        break;
      default:
        backgroundColor = 'var(--cwv-bg-primary)';
        borderColor = 'var(--cwv-border-color)';
        textColor = 'var(--cwv-text-primary)';
    }

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
      backgroundColor,
      border: `2px solid ${borderColor}`,
      color: textColor,
      cursor: isDragging ? (isCopyMode ? 'copy' : 'grabbing') : (canEditAppointments && appointment.status !== 'completed' ? (isCopyMode ? 'copy' : 'grab') : 'pointer')
    };

    const handleClick = (e) => {
      if (!isDragging) {
        e.stopPropagation();
        handleAppointmentClick(appointment);
      }
    };

    const handleDoubleClick = (e) => {
      if (!isDragging) {
        e.stopPropagation();
        handleAppointmentDoubleClick(appointment);
      }
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        className={`cwv-appointment-card ${isDragging ? 'cwv-dragging' : ''} ${canEditAppointments && appointment.status !== 'completed' ? 'cwv-draggable' : ''}`}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
      >
        <div className="cwv-appointment-content" {...listeners}>
          <div className="cwv-appointment-time">
            {`${formatTimeFunc(appointment.startTime)} - ${formatTimeFunc(appointment.endTime)}`}
          </div>
          <div className="cwv-appointment-details">
            {appointment.details || 'Rendez-vous'}
          </div>
          {appointment.notes && appointment.notes.content && (
            <div className="cwv-notes-indicator">
              📝
            </div>
          )}
          {appointment.isRecurring && (
            <div className="cwv-recurring-indicator">
              🔄
            </div>
          )}
        </div>
      </div>
    );
  };

  // Rendu desktop avec cartes verticales
  const renderDesktopView = () => (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext id="global-week-context" items={allAppointmentIds} strategy={rectSortingStrategy}>
        <div className="cwv-container">
          {/* Statistiques de la semaine */}
          <div className="cwv-week-stats">
            <div className="cwv-stat-item">
              <span className="cwv-stat-label">Total rendez-vous:</span>
              <span className="cwv-stat-value">{weekStats.total}</span>
            </div>
            <div className="cwv-stat-item">
              <span className="cwv-stat-label">Assignés:</span>
              <span className="cwv-stat-value cwv-stat-assigned">{weekStats.assigned}</span>
            </div>
            <div className="cwv-stat-item">
              <span className="cwv-stat-label">Non assignés:</span>
              <span className="cwv-stat-value cwv-stat-unassigned">{weekStats.unassigned}</span>
            </div>
            <div className="cwv-stat-item">
              <span className="cwv-stat-label">Heures totales:</span>
              <span className="cwv-stat-value">{weekStats.totalHours}h</span>
            </div>
            <div className="cwv-stat-item">
              <span className="cwv-stat-label">Taux de couverture:</span>
              <span className="cwv-stat-value">{weekStats.coverageRate}%</span>
            </div>
          </div>

          {/* En-tête avec les jours de la semaine */}
          <div className="cwv-week-header">
            <div className="cwv-employee-header">
              <span className="cwv-header-title">Intervenants</span>
            </div>
            {weekDates.map((date, index) => (
              <div
                key={index}
                className={`cwv-day-header ${isToday(date) ? 'cwv-today' : ''}`}
              >
                <div className="cwv-day-name">
                  {date.toLocaleDateString('fr-FR', { weekday: 'short' })}
                </div>
                <div className="cwv-day-number">
                  {date.getDate()}
                </div>
                <div className="cwv-day-month">
                  {date.toLocaleDateString('fr-FR', { month: 'short' })}
                </div>
              </div>
            ))}
          </div>

          <div className="cwv-content">
            {/* Section des plages à combler */}
            <div className="cwv-employee-row cwv-unassigned-row">
              <div className="cwv-employee-info">
                <div className="cwv-employee-avatar cwv-unassigned-avatar">
                  <span className="cwv-avatar-initials">?</span>
                </div>
                <div className="cwv-employee-details">
                  <div className="cwv-employee-name cwv-unassigned-name">
                    Plages à combler
                  </div>
                  <div className="cwv-employee-count">
                    {appointments.filter(app => !app.employeeId || app.status === 'libre').length} rendez-vous
                  </div>
                </div>
              </div>
              {weekDates.map((date, dayIndex) => (
                <DroppableRow
                  key={dayIndex}
                  id={`unassigned-${dayIndex}`}
                  className={`cwv-day-column ${isToday(date) ? 'cwv-today' : ''} ${canEditAppointments ? 'cursor-pointer' : ''}`}
                  onClick={() => handleCellClick(date, null)}
                >
                  <div className="cwv-appointments-list">
                    {getUnassignedAppointmentsForDay(date)
                      .sort((a, b) => a.startTime.localeCompare(b.startTime))
                      .map((appointment) => (
                        <AppointmentCard
                          key={appointment.id}
                          appointment={appointment}
                          employee={null}
                        />
                      ))}
                  </div>
                </DroppableRow>
              ))}
            </div>

            {/* Lignes pour les employés */}
            {employees.map(employee => (
              <div key={employee.userId} className="cwv-employee-row">
                <div className="cwv-employee-info">
                  <div className="cwv-employee-avatar" style={{ backgroundColor: employee.color }}>
                    {employee.photoURL ? (
                      <img
                        src={employee.photoURL}
                        alt={employee.name || `${employee.firstName} ${employee.lastName}`}
                        className="cwv-avatar-image"
                      />
                    ) : (
                      <span className="cwv-avatar-initials" style={{ color: isLightColor(employee.color) ? '#1F2937' : '#F9FAFB' }}>
                        {getInitials(employee.name || `${employee.firstName} ${employee.lastName}`)}
                      </span>
                    )}
                  </div>
                  <div className="cwv-employee-details">
                    <div className="cwv-employee-name">
                      {employee.name || `${employee.firstName} ${employee.lastName}`}
                    </div>
                    <div className="cwv-employee-count">
                      {appointments.filter(app => app.employeeId === employee.userId).length} rendez-vous
                    </div>
                  </div>
                </div>
                {weekDates.map((date, dayIndex) => (
                  <DroppableRow
                    key={dayIndex}
                    id={`${employee.userId}-${dayIndex}`}
                    className={`cwv-day-column ${isToday(date) ? 'cwv-today' : ''} ${canEditAppointments && !isEmployeeOnLeave(employee.userId, date) ? 'cursor-pointer' : ''}`}
                    onClick={() => !isEmployeeOnLeave(employee.userId, date) && handleCellClick(date, employee.userId)}
                  >
                    {isEmployeeOnLeave(employee.userId, date) ? (
                      <div className="cwv-leave-block">
                        <Clock className="cwv-leave-icon" size={16} />
                        <span className="cwv-leave-text">En congé</span>
                      </div>
                    ) : (
                      <div className="cwv-appointments-list">
                        {getEmployeeAppointmentsForDay(employee.userId, date)
                          .sort((a, b) => a.startTime.localeCompare(b.startTime))
                          .map((appointment) => (
                            <AppointmentCard
                              key={appointment.id}
                              appointment={appointment}
                              employee={employee}
                            />
                          ))}
                      </div>
                    )}
                  </DroppableRow>
                ))}
              </div>
            ))}
          </div>

          {canEditAppointments && (
            <button
              onClick={() => {
                setSelectedAppointment(null);
                setSelectedDate(currentDate);
                setSelectedEmployee(null);
                setIsModalOpen(true);
              }}
              className="cwv-add-button"
              title="Ajouter un rendez-vous"
            >
              <Plus size={24} />
            </button>
          )}
        </div>
      </SortableContext>

      {/* DragOverlay pour un meilleur feedback visuel */}
      {createPortal(
        <DragOverlay>
          {activeAppointment ? (
            <div
              className={`cwv-appointment-card cwv-drag-overlay ${activeAppointment ? getStatusClass(activeAppointment.status) : ''}`}
              style={{
                backgroundColor: isCopyMode ? '#10B981' : undefined,
                border: isCopyMode ? '2px solid #059669' : undefined,
                transform: 'rotate(2deg) scale(1.05)',
                boxShadow: '0 12px 30px rgba(0, 0, 0, 0.4)',
                cursor: isCopyMode ? 'copy' : 'grabbing',
                pointerEvents: 'none' // Ne pas bloquer la détection de collision
              }}
            >
              <div className="cwv-appointment-content" style={{ color: isCopyMode ? 'white' : undefined }}>
                {isCopyMode && (
                  <div className="cwv-copy-badge" style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '-8px',
                    backgroundColor: '#059669',
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    📋 Copie
                  </div>
                )}
                <div className="cwv-appointment-time" style={{ color: isCopyMode ? 'white' : undefined }}>
                  {`${formatTimeFunc(activeAppointment.startTime)} - ${formatTimeFunc(activeAppointment.endTime)}`}
                </div>
                <div className="cwv-appointment-details" style={{ color: isCopyMode ? 'white' : undefined }}>
                  {activeAppointment.details || 'Rendez-vous'}
                </div>
              </div>
            </div>
          ) : null}
        </DragOverlay>,
        document.body
      )}
    </DndContext>
  );

  // Rendu mobile simplifié
  const renderMobileView = () => (
    <div className="cwv-mobile-container">
      <div className="cwv-mobile-week-view">
        {weekDates.map((date, index) => (
          <div key={index} className={`cwv-mobile-day ${isToday(date) ? 'cwv-today' : ''}`}>
            <div
              className="cwv-mobile-day-header"
            >
              <div className="cwv-mobile-day-name">
                {date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
              </div>
              {isToday(date) && <span className="cwv-today-badge">Aujourd'hui</span>}
            </div>

            {/* Rendez-vous non assignés */}
            {getUnassignedAppointmentsForDay(date).length > 0 && (
              <div className="cwv-mobile-section">
                <div className="cwv-mobile-section-header cwv-unassigned">
                  <AlertCircle size={16} />
                  <span>Plages à combler ({getUnassignedAppointmentsForDay(date).length})</span>
                </div>
                {getUnassignedAppointmentsForDay(date).map((appointment) => (
                  <div
                    key={appointment.id}
                    className="cwv-mobile-appointment cwv-mobile-unassigned"
                    onClick={() => handleAppointmentClick(appointment)}
                  >
                    <div className="cwv-mobile-appointment-time">
                      <Clock size={14} />
                      {`${formatTimeFunc(appointment.startTime)} - ${formatTimeFunc(appointment.endTime)}`}
                    </div>
                    {appointment.details && (
                      <div className="cwv-mobile-appointment-details">
                        {appointment.details}
                      </div>
                    )}
                    {appointment.notes && appointment.notes.content && (
                      <div className="cwv-mobile-notes-indicator">📝 Note</div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Rendez-vous par employé */}
            {employees.map(employee => {
              const empAppointments = getEmployeeAppointmentsForDay(employee.userId, date);
              if (empAppointments.length === 0 && !isEmployeeOnLeave(employee.userId, date)) return null;

              return (
                <div key={employee.userId} className="cwv-mobile-section">
                  <div className="cwv-mobile-section-header" style={{ borderLeftColor: employee.color }}>
                    <div className="cwv-mobile-employee-avatar" style={{ backgroundColor: employee.color }}>
                      {employee.photoURL ? (
                        <img src={employee.photoURL} alt={employee.name} className="cwv-avatar-image" />
                      ) : (
                        <span style={{ color: isLightColor(employee.color) ? '#1F2937' : '#F9FAFB' }}>
                          {getInitials(employee.name || `${employee.firstName} ${employee.lastName}`)}
                        </span>
                      )}
                    </div>
                    <span>{employee.name || `${employee.firstName} ${employee.lastName}`}</span>
                    <span className="cwv-mobile-count">({empAppointments.length})</span>
                  </div>

                  {isEmployeeOnLeave(employee.userId, date) ? (
                    <div className="cwv-mobile-leave">
                      <Clock size={14} />
                      <span>En congé</span>
                    </div>
                  ) : (
                    empAppointments.map((appointment) => (
                      <div
                        key={appointment.id}
                        className={`cwv-mobile-appointment ${getStatusClass(appointment.status)}`}
                        style={{ borderLeftColor: employee.color }}
                        onClick={() => handleAppointmentClick(appointment)}
                      >
                        <div className="cwv-mobile-appointment-time">
                          <Clock size={14} />
                          {`${formatTimeFunc(appointment.startTime)} - ${formatTimeFunc(appointment.endTime)}`}
                        </div>
                        {appointment.details && (
                          <div className="cwv-mobile-appointment-details">
                            {appointment.details}
                          </div>
                        )}
                        <div className="cwv-mobile-appointment-footer">
                          {appointment.status === 'completed' && (
                            <span className="cwv-mobile-status-badge cwv-completed">
                              <Check size={12} /> Terminé
                            </span>
                          )}
                          {appointment.notes && appointment.notes.content && (
                            <div className="cwv-mobile-notes-indicator">📝</div>
                          )}
                          {appointment.isRecurring && (
                            <div className="cwv-mobile-recurring-indicator">🔄</div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              );
            })}

            {canEditAppointments && (
              <button
                onClick={() => {
                  setSelectedDate(date);
                  setSelectedEmployee(null);
                  setIsModalOpen(true);
                }}
                className="cwv-mobile-add-button"
              >
                <Plus size={20} />
                <span>Ajouter un rendez-vous</span>
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <>
      {isMobile ? renderMobileView() : renderDesktopView()}

      {/* Modals */}
      {isModalOpen && (
        <ClientAddAppointmentModal
          date={selectedDate || currentDate}
          employees={employees}
          onSave={handleAddAppointment}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedDate(null);
            setSelectedEmployee(null);
          }}
          initialEmployeeId={selectedEmployee}
        />
      )}

      {isViewModalOpen && selectedAppointment && (
        <AppointmentViewModal
          appointment={selectedAppointment}
          employees={employees}
          onClose={() => {
            setIsViewModalOpen(false);
            setSelectedAppointment(null);
          }}
          onEdit={() => {
            setIsViewModalOpen(false);
            setIsEditModalOpen(true);
          }}
          onDelete={handleDeleteAppointment}
          hasPermission={hasPermission}
        />
      )}

      {isEditModalOpen && selectedAppointment && (
        <ClientEditAppointmentModal
          appointment={selectedAppointment}
          employees={employees}
          onSave={handleUpdateAppointment}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedAppointment(null);
          }}
          onDelete={handleDeleteAppointment}
          canEditAppointments={canEditAppointments}
        />
      )}

      {showRecurrenceMoveModal && pendingMoveData && (
        <RecurrenceMoveModal
          isOpen={showRecurrenceMoveModal}
          onClose={() => {
            setShowRecurrenceMoveModal(false);
            setPendingMoveData(null);
          }}
          onConfirm={handleRecurrenceMoveConfirm}
          appointment={pendingMoveData.appointment}
        />
      )}

      {showRecurrenceEditModal && pendingUpdateData && (
        <RecurrenceEditModal
          isOpen={showRecurrenceEditModal}
          onClose={() => {
            setShowRecurrenceEditModal(false);
            setPendingUpdateData(null);
          }}
          onConfirm={handleRecurrenceEditConfirm}
          appointment={pendingUpdateData}
        />
      )}
    </>
  );
});

// Ajouter le displayName pour le débogage
ClientWeekView.displayName = 'ClientWeekView';

export default ClientWeekView;
